import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, Badge, Spinner, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PageTitle from "../../layouts/PageTitle";
import usePermission from "../../../hooks/usePermission";
import { getIdleReport } from "../../../services/equipmentService";

const STATUS_COLORS = {
 Available: "success",
 Idle: "secondary",
};

const IdleEquipmentReport = () => {
 const navigate = useNavigate();
 const { hasPermission } = usePermission();
 const canManage = hasPermission(["create:equipment", "edit:equipment", "delete:equipment"]);

 const [items, setItems] = useState([]);
 const [loading, setLoading] = useState(true);
 const [idleDays, setIdleDays] = useState(7);

 const fetchReport = useCallback(async () => {
 setLoading(true);
 try {
 const res = await getIdleReport({ idleDays });
 setItems(res.data?.data || []);
 } catch (_) {
 toast.error("Failed to load idle report");
 } finally {
 setLoading(false);
 }
 }, [idleDays]);

 useEffect(() => {
 fetchReport();
 }, [fetchReport]);

 const getIdleBadge = (days) => {
 if (days >= 30) return "danger";
 if (days >= 14) return "warning";
 return "secondary";
 };

 return (
 <>
 <PageTitle activeMenu="Idle Equipment Report" motherMenu="Equipment" />

 <Card className="shadow-sm">
 <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
 <h5 className="mb-0">
 <i className="fas fa-pause-circle me-2 text-warning" />
 Idle Equipment Report
 </h5>
 <div className="d-flex align-items-center gap-2">
 <Form.Label className="mb-0 text-nowrap small">Idle more than</Form.Label>
 <Form.Control
 type="number"
 value={idleDays}
 onChange={(e) => setIdleDays(Math.max(1, parseInt(e.target.value) || 1))}
 style={{ width: 80 }}
 size="sm"
 min={1}
 />
 <Form.Label className="mb-0 text-nowrap small">days</Form.Label>
 <Button size="sm" variant="outline-primary" onClick={fetchReport}>
 <i className="fas fa-sync me-1" />Refresh
 </Button>
 <Button size="sm" variant="outline-secondary" onClick={() => navigate("../equipment")}>
 <i className="fas fa-arrow-left me-1" />Back
 </Button>
 </div>
 </Card.Header>

 {loading ? (
 <Card.Body className="text-center py-5">
 <Spinner animation="border" variant="warning" />
 </Card.Body>
 ) : items.length === 0 ? (
 <Card.Body className="text-center py-5 text-muted">
 <i className="fas fa-check-circle fa-3x mb-3 d-block text-success" />
 No equipment has been idle for more than {idleDays} days.
 </Card.Body>
 ) : (
 <>
 <Card.Body className="border-bottom pb-2">
 <Row>
 <Col>
 <div className="alert alert-warning py-2 mb-0 d-flex align-items-center gap-2">
 <i className="fas fa-exclamation-triangle" />
 <span>
 <strong>{items.length}</strong> equipment items have been idle for more than {idleDays} days.
 Consider reassigning or scheduling maintenance.
 </span>
 </div>
 </Col>
 </Row>
 </Card.Body>

 <div className="table-responsive">
 <table className="table table-hover align-middle mb-0">
 <thead className="table-light">
 <tr>
 <th>Code</th>
 <th>Name</th>
 <th>Category</th>
 <th>Status</th>
 <th>Last Project</th>
 <th>Location</th>
 <th>Idle Since</th>
 <th>Total Hours</th>
 <th>Maintenance</th>
 <th>Actions</th>
 </tr>
 </thead>
 <tbody>
 {items.map((eq) => (
 <tr key={eq._id}>
 <td>
 <span className="badge bg-light text-dark border">{eq.equipmentCode}</span>
 </td>
 <td className="fw-semibold">{eq.name}</td>
 <td>{eq.category}</td>
 <td>
 <Badge bg={STATUS_COLORS[eq.status] || "secondary"}>
 {eq.status}
 </Badge>
 </td>
 <td>{eq.project?.projectName || "—"}</td>
 <td>{eq.location || "—"}</td>
 <td>
 <Badge bg={getIdleBadge(eq.idleDays)}>
 {eq.idleDays} days
 </Badge>
 </td>
 <td>{eq.totalUtilizationHours || 0} h</td>
 <td>
 <Badge
 bg={
 eq.maintenanceStatus === "Overdue"
 ? "danger"
 : eq.maintenanceStatus === "Due Soon"
 ? "warning"
 : "success"
 }
 >
 {eq.maintenanceStatus}
 </Badge>
 </td>
 <td>
 <div className="d-flex gap-1">
 <Button
 size="sm"
 variant="outline-info"
 onClick={() => navigate(`../equipment/view/${eq._id}`)}
 >
 <i className="fas fa-eye" />
 </Button>
 {canManage && (
 <Button
 size="sm"
 variant="outline-primary"
 onClick={() => navigate(`../equipment/deploy/${eq._id}`)}
 >
 <i className="fas fa-truck" />
 </Button>
 )}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </>
 )}
 </Card>
 </>
 );
};

export default IdleEquipmentReport;
